import { useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import {
  ButtonTertiary,
  IconMore,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverNavigationLink,
  PopoverTrigger,
  TableCell,
  TableRow as TableRowEncore,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { ArtistAlbum } from '@hooks/useArtistAppearsOn/types';
import { useClipboard } from '@hooks/useClipboard';
import { useLiveInTerritoryAvailability } from '@hooks/useLiveInTerritoryAvailability';
import { useModalPopoverFocus } from '@hooks/useModalPopoverFocus';
import { usePopoverFocus } from '@hooks/usePopoverFocus';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import { StyledNameAndExplicitTagContainer } from '@components/Release/TracksTab/styles';
import { LiveInTerritories } from '@components/shared/LiveInTerritories';
import { AnchorWithHover, ExplicitTag } from '@components/shared/styles';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import {
  convertToTitleCase,
  formatCasingOfReleaseType,
  isEmptyString,
} from '@helpers/utils';
import { ArtistRelocationModal } from '../ArtistRelocationModal';
const StyledPopoverNavigationLink = styled(PopoverNavigationLink)`
  cursor: default;
`;

const StyledTextLink = styled(TextLink)`
  cursor: pointer;
`;

const StyledTrackNameDetailsContainer = styled(TableCell)`
  display: flex;
  flex-direction: row;
`;
const ExplicitTagWrapper = styled.span`
  margin-left: -7px;
`;

export const StyledTrackNameDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

interface TableRowProps {
  album: ArtistAlbum;
}

export const TableRow = ({ album }: TableRowProps) => {
  const [showExpandedRow, toggleShowExpandedRow] = useState(false);
  const [showArtistRelocationModal, toggleShowArtistRelocationModal] =
    useState(false);

  const albumUri = album.uri;
  const uriParts = albumUri.split(':');
  const uriNumber = uriParts[uriParts.length - 1];
  const albumUriID = albumUri.replace('spotify:album:', '');

  const {
    data: liveInTerritoriesData,
    isLoading: isLoadingTerritoryAvailability,
    error,
    mutate,
    isValidating,
  } = useLiveInTerritoryAvailability(
    showExpandedRow && typeof uriNumber === 'string' ? uriNumber : '',
  );

  const showLiveInTerritoriesData = showExpandedRow && !!liveInTerritoriesData;

  const { copyText: copyUri } = useClipboard(albumUri);
  const { copyText: copyUrl } = useClipboard(album.url);
  const [, dispatch] = useSnackbarNotifications();
  const t = useT();

  const handleExpandTerritoriesClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleShowExpandedRow(prevShowExpandedRow => {
      sendEvent({
        eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
        eventAction: EVENT_ACTION.TOGGLE_ROW,
        eventLabel: `Expanded: ${!prevShowExpandedRow}`,
        product: GA_PRODUCT_CATALOG,
      });

      return !prevShowExpandedRow;
    });
  };

  const handleLiveInTerritoriesApiRetry = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
      eventAction: EVENT_ACTION.TOGGLE_ROW,
      eventLabel: `Retried live in territories info fetch`,
      product: GA_PRODUCT_CATALOG,
    });

    mutate();
  };

  const copyText = t(
    'atlas.snackbars.copied_to_clipboard',
    'Copied to clipboard',
    'Snackbar message to indicate copied to clipboard',
  );

  const notThisArtistsReleaseText = t(
    'atlas.relocation.not_this_artists_release',
    'Not this artist’s release?',
    `Text to guide user if this is not their artist's release`,
  );

  const {
    triggerBtnRef,
    firstInteractiveEleRef,
    lastInteractiveEleRef,
    toggleModal,
  } = useModalPopoverFocus(showArtistRelocationModal);
  const {
    isOpen: showPopover,
    toggleOn,
    toggleOff,
    openBtnRef,
    firstInteractiveEleRef: firstPopoverNavItemRef,
  } = usePopoverFocus();
  return (
    <>
      <TableRowEncore>
        <TableCell>
          <StyledTrackNameDetailsContainer>
            <StyledTrackNameDetails>
              <StyledNameAndExplicitTagContainer>
                <Link
                  href={{
                    pathname: `/release/${albumUriID}`,
                  }}
                  passHref
                  legacyBehavior
                >
                  <AnchorWithHover
                    onClick={evt => {
                      evt.stopPropagation();
                    }}
                  >
                    <Type variant="violaBold" semanticColor="textBrightAccent">
                      {album.name}
                    </Type>
                  </AnchorWithHover>
                </Link>
                {album.hasAtLeastOneExplicitTrack && (
                  <ExplicitTagWrapper>
                    <ExplicitTag
                      aria-label={t(
                        'atlas.labels.explicit_lyrics',
                        'Explicit lyrics',
                        'Label for Explicit lyrics. The value will be a boolean, i.e. "Yes" or "No".',
                      )}
                    />
                  </ExplicitTagWrapper>
                )}
              </StyledNameAndExplicitTagContainer>
              {album.titleVersion && !isEmptyString(album.titleVersion) && (
                <Type variant="viola" semanticColor="textSubdued">
                  {convertToTitleCase(album.titleVersion)}
                </Type>
              )}
            </StyledTrackNameDetails>
          </StyledTrackNameDetailsContainer>
        </TableCell>
        <TableCell>
          <Type semanticColor="textBase">
            {
              // left untranslated by request of COPS
              formatCasingOfReleaseType(album.type)
            }
          </Type>
        </TableCell>
        <TableCell>
          <Type semanticColor="textBase">{album.feedName}</Type>
        </TableCell>
        <TableCell>
          <Type semanticColor="textBase">{album.licensorName}</Type>
        </TableCell>
        <TableCell>
          <Type semanticColor="textBase">{album.labelName}</Type>
        </TableCell>
        <TableCell>
          <Type semanticColor="textBase">{album.originalReleaseDate}</Type>
        </TableCell>
        <TableCell>
          {showExpandedRow && (
            <LiveInTerritories
              isLoading={isLoadingTerritoryAvailability || isValidating}
              error={error}
              data={liveInTerritoriesData}
              handleRetry={handleLiveInTerritoriesApiRetry}
            />
          )}
          {!isValidating && !error && (
            <StyledTextLink
              semanticColor="textBrightAccent"
              tabIndex={0}
              standalone
              component="button"
              onClick={handleExpandTerritoriesClick}
            >
              {showLiveInTerritoriesData
                ? t(
                    'atlas.actions.collapse_view',
                    'Collapse view',
                    'Text button to collapse expanded row',
                  )
                : t(
                    'atlas.actions.expand_view',
                    'Expand to view',
                    'Text button to expand row',
                  )}
            </StyledTextLink>
          )}
        </TableCell>
        <TableCell>
          <PopoverTrigger
            hideCloseButton
            placement="start"
            onShow={() => toggleOn()}
            onHide={() => {
              if (showPopover) {
                toggleOff();
              }
            }}
            overlay={
              showPopover && (
                <PopoverNavigation arrow="end">
                  <PopoverNavigationItem>
                    <PopoverNavigationLink
                      ref={firstPopoverNavItemRef}
                      component="button"
                      onClick={() => {
                        sendEvent({
                          eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Release URI copied',
                          product: GA_PRODUCT_CATALOG,
                        });

                        copyUri();
                        dispatch(buildAddSnackbarAction(copyText));
                        toggleOff();
                      }}
                    >
                      {t(
                        'atlas.actions.copy_release_uri',
                        'Copy Release URI',
                        'Text for Copy Release URI action',
                      )}
                    </PopoverNavigationLink>
                  </PopoverNavigationItem>
                  <PopoverNavigationItem>
                    <PopoverNavigationLink
                      component="button"
                      onClick={() => {
                        sendEvent({
                          eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Release URL copied',
                          product: GA_PRODUCT_CATALOG,
                        });

                        copyUrl();
                        dispatch(buildAddSnackbarAction(copyText));
                        toggleOff();
                      }}
                    >
                      {t(
                        'atlas.actions.copy_release_url',
                        'Copy Release URL',
                        'Text for Copy Release URL action',
                      )}
                    </PopoverNavigationLink>
                  </PopoverNavigationItem>
                  <PopoverNavigationItem>
                    <StyledPopoverNavigationLink
                      onClick={() => {
                        sendEvent({
                          eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
                          eventAction: EVENT_ACTION.CLICK,
                          eventLabel: 'Artist Relocation Form Modal Opened',
                          product: GA_PRODUCT_CATALOG,
                        });
                        toggleOff();
                        toggleShowArtistRelocationModal(true);
                      }}
                    >
                      {notThisArtistsReleaseText}
                    </StyledPopoverNavigationLink>
                  </PopoverNavigationItem>
                </PopoverNavigation>
              )
            }
          >
            <ButtonTertiary
              condensedAll
              buttonSize="lg"
              iconOnly={IconMore}
              aria-expanded={showPopover}
              aria-label="View actions"
              ref={showArtistRelocationModal ? triggerBtnRef : openBtnRef}
            />
          </PopoverTrigger>
        </TableCell>
      </TableRowEncore>
      {showArtistRelocationModal && (
        <ArtistRelocationModal
          firstBtnRef={firstInteractiveEleRef}
          lastBtnRef={lastInteractiveEleRef}
          onClose={() => {
            triggerBtnRef.current?.focus();
            toggleShowArtistRelocationModal(false);
            toggleModal();
          }}
          uri={albumUri}
        />
      )}
    </>
  );
};
